import { Permission } from '@procureai/permissions';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgSupplierView } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  GridAlignmentType,
  gridColDefFactory,
  LinkCell,
  LinkCellValue,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { RouteId } from '~/routes/RouteId';

export enum VetSuppliersResultDataGridField {
  NAME = 'name',
  ACTIVE_CONTRACT_COUNT = 'activeContractCount',
  PURCHASE_ORDER_VALUE_12M = 'purchaseOrderValueSum12m',
  CREATED_AT = 'createdAt',
}

export const useVetSuppliersResultDataGrid: UseDataGridConfig<SvbrgSupplierView> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'VetSuppliersResultDataGrid.column',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({
      tColumn,
      t,
      defaultOptions: { sortable: false, align: GridAlignmentType.LEFT, minWidth: 120 },
    });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: VetSuppliersResultDataGridField.NAME,
          renderCell: cellFactory(LinkCell, { needsPermission: Permission.BaseSupplierList }),
          valueGetter: (_value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.supplierDetail(row.id),
            label: row.name,
          }),
          hideable: false,
          minWidth: 200,
          flex: 1,
        }),
        createColumn({
          field: VetSuppliersResultDataGridField.ACTIVE_CONTRACT_COUNT,
          align: GridAlignmentType.RIGHT,
          minWidth: 150,
        }),
        createColumn({
          field: VetSuppliersResultDataGridField.PURCHASE_ORDER_VALUE_12M,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, {
            currency: DEFAULT_CURRENCY,
          }),
          minWidth: 250,
        }),
        createColumn({
          field: VetSuppliersResultDataGridField.CREATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 120,
        }),
      ],
    } as DataGridConfig<SvbrgSupplierView>;
  }, [tColumn, t]);
};
